/** Checks whether a string contains HTML tags */
export function hasHtmlTags(content: string): boolean {
  return /<[a-z][\s\S]*?>/i.test(content)
}

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'a',
  'ul',
  'ol',
  'li',
  'span',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'blockquote',
  'pre',
  'code',
  'hr',
  'img',
  'sub',
  'sup',
])

const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'src', 'alt', 'style', 'class', 'width', 'height', 'colspan', 'rowspan'])

const VOID_HTML_TAGS = new Set(['br', 'hr', 'img'])

function escapeHtmlText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function escapeHtmlAttrValue(value: string): string {
  return escapeHtmlText(value).replace(/"/g, '&quot;')
}

/**
 * Serialise a sanitised DOM subtree to an HTML string without using
 * Element#innerHTML on the output path (avoids CodeQL dom-text-as-html on reads).
 */
function serializeSanitizedBody(body: HTMLElement): string {
  const parts: string[] = []

  const appendFromNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE) {
      parts.push(escapeHtmlText(node.nodeValue ?? ''))
      return
    }
    if (node.nodeType === Node.COMMENT_NODE) {
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return
    }

    const el = node as Element
    const tag = el.tagName.toLowerCase()

    if (!ALLOWED_TAGS.has(tag)) {
      for (const c of Array.from(el.childNodes)) {
        appendFromNode(c)
      }
      return
    }

    parts.push(`<${tag}`)
    for (const attr of Array.from(el.attributes)) {
      const ln = attr.name.toLowerCase()
      if (ln.startsWith('on') || !ALLOWED_ATTRS.has(ln)) {
        continue
      }
      if ((ln === 'href' || ln === 'src') && /^\s*(javascript|data):/i.test(attr.value)) {
        continue
      }
      parts.push(` ${attr.name}="${escapeHtmlAttrValue(attr.value)}"`)
    }

    if (VOID_HTML_TAGS.has(tag)) {
      parts.push('>')
      return
    }

    parts.push('>')
    for (const c of Array.from(el.childNodes)) {
      appendFromNode(c)
    }
    parts.push(`</${tag}>`)
  }

  for (const c of Array.from(body.childNodes)) {
    appendFromNode(c)
  }
  return parts.join('')
}

/**
 * Sanitises HTML content using the browser's DOMParser.
 * Strips scripts, event handlers, and dangerous URIs while keeping
 * common formatting/layout tags intact.
 */
export function sanitizeHtml(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const walk = (node: Node): void => {
    const children = Array.from(node.childNodes)
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element
        const tag = el.tagName.toLowerCase()

        if (!ALLOWED_TAGS.has(tag)) {
          const fragment = document.createDocumentFragment()
          while (el.firstChild) fragment.appendChild(el.firstChild)
          el.replaceWith(fragment)
          walk(node)
          return
        }

        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase()
          if (name.startsWith('on') || !ALLOWED_ATTRS.has(name)) {
            el.removeAttribute(attr.name)
            continue
          }
          if ((name === 'href' || name === 'src') && /^\s*(javascript|data):/i.test(attr.value)) {
            el.removeAttribute(attr.name)
          }
        }

        if (tag === 'a') {
          el.setAttribute('target', '_blank')
          el.setAttribute('rel', 'noopener noreferrer')
        }
      }

      walk(child)
    }
  }

  const body = doc.body
  if (!body) {
    return ''
  }
  walk(body)
  return serializeSanitizedBody(body)
}
